import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const TermsAndConditions = ({ onAccept, isModal = false }) => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    fetchLatestTerms();
  }, []);

  const fetchLatestTerms = async () => {
    try {
      const response = await api.get('/terms/latest');
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load terms and conditions');
      console.error('Error fetching terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
    setScrolledToBottom(isAtBottom);
  };

  const handleAccept = async () => {
    if (!scrolledToBottom && !isModal) {
      toast.error('Please read the entire terms and conditions before accepting');
      return;
    }

    setAccepting(true);
    try {
      await api.post('/terms/accept', { termsId: terms.id });
      toast.success('Terms and conditions accepted');
      
      if (onAccept) {
        onAccept();
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to accept terms and conditions');
      console.error('Error accepting terms:', error);
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    if (isModal) {
      // In modal mode, log the user out
      logout();
      toast.success('You have been logged out');
      navigate('/');
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!terms) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Terms and conditions not available</p>
      </div>
    );
  }

  const content = (
    <div className={`${isModal ? '' : 'max-w-4xl mx-auto px-4 py-8'}`}>
      <div className={`bg-white rounded-lg shadow-lg ${isModal ? '' : 'p-8'}`}>
        <h1 className="text-3xl font-bold text-gray-800 mb-6">{terms.title}</h1>
        
        <div className="mb-4 text-sm text-gray-600">
          <p>Version: {terms.version}</p>
          <p>Effective Date: {new Date(terms.effective_date).toLocaleDateString()}</p>
        </div>

        <div 
          className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto mb-6 border border-gray-200"
          onScroll={handleScroll}
        >
          <div className="prose prose-sm max-w-none">
            {terms.content.split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {!isModal && !scrolledToBottom && (
          <p className="text-sm text-amber-600 mb-4 text-center">
            Please scroll to the bottom to read all terms before accepting
          </p>
        )}

        <div className="flex gap-4 justify-center">
          <button
            onClick={handleDecline}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200"
          >
            {isModal ? 'Logout' : 'Decline'}
          </button>
          <button
            onClick={handleAccept}
            disabled={(!scrolledToBottom && !isModal) || accepting}
            className={`px-6 py-3 rounded-lg transition duration-200 ${
              (!scrolledToBottom && !isModal) || accepting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {accepting ? 'Accepting...' : 'I Accept'}
          </button>
        </div>
      </div>
    </div>
  );

  return isModal ? content : (
    <div className="min-h-screen bg-gray-100">
      {content}
    </div>
  );
};

export default TermsAndConditions;